import type { BaseLogEvent } from "./logger-core";
import { getItem, removeItem, setItem } from "@/lib/storage-utils";
import { STORAGE, STORAGE_KEY } from "@/constants";

const DB_NAME = "AppLoggerDB";
const DB_VERSION = 1.0;
const STORE_NAME = "logs";

type PersistedLogBatch = {
  id: string;
  instanceId: string;
  timestamp: number;
  events: BaseLogEvent[];
};

const SENSITIVE_KEYS = new Set([
  "password",
  "confirm_password",
  "confirmpassword",
  "current_password",
  "currentpassword",
  "new_password",
  "newpassword",
  "ssn",
  "email",
  "dob",
  "firstname",
  "lastname",
  "address",
  "zip",
  "zipcode",
  "access_token",
  "refresh_token",
  "token",
  "authorization",
  "sid",
]);

function redactValue(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;

  if (Array.isArray(value)) {
    return value.map((item) => redactValue(item));
  }

  const input = value as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  for (const [key, raw] of Object.entries(input)) {
    const normalizedKey = key.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

    if (key.toLowerCase() === "formdata" && raw && typeof raw === "object") {
      const redactedFormData: Record<string, unknown> = {};
      for (const formKey of Object.keys(raw)) {
        redactedFormData[formKey] = "[REDACTED]";
      }
      out[key] = redactedFormData;
      continue;
    }

    if (
      SENSITIVE_KEYS.has(normalizedKey) ||
      SENSITIVE_KEYS.has(key.toLowerCase())
    ) {
      out[key] = "[REDACTED]";
      continue;
    }

    out[key] = redactValue(raw);
  }

  return out;
}

function sanitizeLogsForPersistence(logs: BaseLogEvent[]): BaseLogEvent[] {
  return logs.map((event) => ({
    ...event,
    context: (redactValue(event.context) as Record<string, unknown>) ?? {},
  }));
}

class LoggerStorage {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  // initalize Indexed DB
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("IndexedDB open failed", request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        // console.log("IndexedDB initialized successfully");
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
          store.createIndex("timestamp", "timestamp", { unique: false });
          store.createIndex("sessionId", "sessionId", { unique: false });
          // console.log("IndexedDB store created")
        }
      };
    });

    return this.initPromise;
  }

  // save logs to Indexed DB
  async saveLogs(logs: BaseLogEvent[], instanceId?: string): Promise<void> {
    if (logs.length === 0) return;
    const sanitizedLogs = sanitizeLogsForPersistence(logs);

    try {
      await this.init();

      if (!this.db) throw new Error("IndexedDB not available");

      const batch: PersistedLogBatch = {
        id: crypto.randomUUID(),
        instanceId: instanceId ?? "unknown",
        timestamp: Date.now(),
        events: sanitizedLogs,
      };

      return new Promise((resolve, reject) => {
        const transaction = this.db?.transaction(STORE_NAME, "readwrite");
        const store = transaction?.objectStore(STORE_NAME);
        const request = store?.put(batch);

        if (request) {
          request.onsuccess = () => {
            // console.log("Saved in DB", sanitizedLogs?.length, "logs to IndexedDB, batch ID: ", batch?.id);
            resolve();
          };

          request.onerror = () => {
            console.error("IndexedDB save failed", request.error);
            reject(request?.error);
          };
        }
      });
    } catch (error) {
      console.error("Failed to save to IndexedDB", error);
      this.saveToEmergencyCache(sanitizedLogs, instanceId);
    }
  }

  // load all persisted log batches
  async loadAllLogs(): Promise<PersistedLogBatch[]> {
    try {
      await this.init();

      if (!this.db) throw new Error("IndexedDB is not available");

      return new Promise((resolve, reject) => {
        const transaction = this.db?.transaction(STORE_NAME, "readonly");
        const store = transaction?.objectStore(STORE_NAME);
        const request = store?.getAll();

        if (request) {
          request.onsuccess = () => {
            const batches = request.result as PersistedLogBatch[];

            const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;

            const validBatches = batches.filter(
              (batch) => batch.timestamp > weekAgo,
            );

            // console.log(
            //    "Loaded",
            //    validBatches.length,
            //    "batches from IndexedDB"
            // );

            if (batches.length !== validBatches.length) {
              this.cleanUpOldBatches(weekAgo);
            }

            resolve(validBatches);
          };

          request.onerror = () => {
            console.error("IndexedDB load failed", request.error);
            reject(request.error);
          };
        }
      });
    } catch (error) {
      console.error("Failed to load all logs from IndexedDB", error);
      return this.loadFromEmergencyCache();
    }
  }

  // delete specified batch by ID
  async deleteBatch(batchID: string): Promise<void> {
    try {
      await this.init();

      if (!this.db) throw new Error("IndexedDB is not available");

      return new Promise((resolve, reject) => {
        const transaction = this.db?.transaction(STORE_NAME, "readwrite");
        const store = transaction?.objectStore(STORE_NAME);
        const request = store?.delete(batchID);

        if (request) {
          request.onsuccess = () => {
            // console.log("Deleted batch", batchID, "from IndexedDB");
            resolve();
          };

          request.onerror = () => {
            console.error("IndexedDB delete failed", request.error);
            reject(request.error);
          };
        }
      });
    } catch (error) {
      console.error("Failed to delete from IndexedDB", error);
    }
  }

  async deleteAll(): Promise<void> {
    try {
      await this.init();

      if (!this.db) throw new Error("IndexedDB is not available");

      return new Promise((resolve, reject) => {
        const transaction = this.db?.transaction(STORE_NAME, "readwrite");
        const store = transaction?.objectStore(STORE_NAME);
        const request = store?.clear();

        if (request) {
          request.onsuccess = () => {
            // console.log("Deleted all logs from IndexedDB");
            resolve();
          };

          request.onerror = () => {
            console.error("IndexedDB delete failed", request.error);
            reject(request.error);
          };
        }
      });
    } catch (error) {
      console.error("Failed to delete all logs from IndexedDB", error);
    }

    return this.clearEmergencyCache();
  }

  // Clean up batches older than cutoff timestamp
  private async cleanUpOldBatches(cutoffTimestamp: number): Promise<void> {
    try {
      await this.init();

      if (!this.db) throw new Error("IndexedDB is not available");

      return new Promise((resolve, reject) => {
        const transaction = this.db?.transaction(STORE_NAME, "readwrite");
        const store = transaction?.objectStore(STORE_NAME);
        const index = store?.index("timestamp");

        // Get records with timestamp < cutoff timestamp
        const range = IDBKeyRange.upperBound(cutoffTimestamp);
        const request = index?.openCursor(range);

        if (request) {
          request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result;
            if (cursor) {
              cursor.delete();
              cursor.continue();
            } else {
              // console.log(
              //    "Cleaned up",
              //    deletedCount,
              //    "old batches from IndexedDB"
              // );
              resolve();
            }
          };

          request.onerror = () => {
            console.error("IndexedDB cleanup failed", request.error);
            reject(request.error);
          };
        }
      });
    } catch (error) {
      console.error("Failed to clean up old batches from IndexedDB", error);
    }
  }

  // Get storage stats(number of batches and total size estimate)
  async getStorageStats(): Promise<{
    batchCount: number;
    totalEvents: number;
  }> {
    try {
      const batches = await this.loadAllLogs();
      const totalEvents = batches.reduce(
        (sum, batch) => sum + batch.events.length,
        0,
      );

      return {
        batchCount: batches.length,
        totalEvents,
      };
    } catch (error) {
      console.error("Failed to get log stats from IndexedDB", error);
      return {
        batchCount: 0,
        totalEvents: 0,
      };
    }
  }

  // EMERGENCY CACHE (localStorage fallback) when IndexedDB fails

  // save logs to local storage
  private saveToEmergencyCache(logs: BaseLogEvent[], instanceId?: string) {
    try {
      const limitedLogs = logs.slice(-10);

      const batch: PersistedLogBatch = {
        id: crypto.randomUUID(),
        instanceId: instanceId ?? "unknown",
        timestamp: Date.now(),
        events: limitedLogs,
      };

      if (JSON.stringify(batch).length > 50 * 1024) {
        console.warn("Emergency cache payload too large, skipping");
        return;
      }

      setItem(STORAGE_KEY.APP_LOGS, batch, STORAGE.LOCAL);
      console.warn(
        "Saved",
        limitedLogs.length,
        "logs to EMERGENCY localStorage cache",
      );
    } catch (error) {
      console.error("Emergency cache save failed", error);
    }
  }

  // load logs from local storage
  private loadFromEmergencyCache(): PersistedLogBatch[] {
    try {
      const storedLogs = getItem(STORAGE_KEY.APP_LOGS, STORAGE.LOCAL);

      if (!storedLogs) return [];

      console.warn("Loaded from EMERGENCY localStorage cache");
      return [storedLogs];
    } catch (error) {
      console.error("Emergency cache load failed", error);
      return [];
    }
  }

  private clearEmergencyCache(): void {
    removeItem(STORAGE_KEY.APP_LOGS, STORAGE.LOCAL);
  }
}

export const loggerStorage = new LoggerStorage();
